
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { TimeSlot } from "@/components/mentor/availability/TimeSlotManager";

export type DaySchedule = {
  day: number;
  slots: TimeSlot[];
};

export const useAvailability = () => {
  const { toast } = useToast();
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [weeklySchedule, setWeeklySchedule] = useState<DaySchedule[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ start: "09:00", end: "18:00" }]);
  const [loading, setLoading] = useState(false);
  const [savingDay, setSavingDay] = useState(false);

  // Load existing availability data when component mounts
  useEffect(() => {
    fetchMentorAvailability();
  }, []);

  const fetchMentorAvailability = async () => {
    try {
      setLoading(true);
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      if (!sessionData?.session?.user?.id) {
        toast({
          title: "Erro",
          description: "Você precisa estar logado para visualizar sua disponibilidade",
          variant: "destructive",
        });
        return;
      }
      
      // Get mentor ID from profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', sessionData.session.user.id)
        .single();
        
      if (profileError || !profileData) {
        toast({
          title: "Erro",
          description: "Não foi possível encontrar seu perfil",
          variant: "destructive",
        });
        return;
      }
      
      // Get mentor info
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('id')
        .eq('profile_id', profileData.id)
        .single();
        
      if (mentorError || !mentorData) {
        toast({
          title: "Erro",
          description: "Não foi possível encontrar suas informações de mentor",
          variant: "destructive",
        });
        return;
      }
      
      // Get mentor availability
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('mentor_availability')
        .select('*')
        .eq('mentor_id', mentorData.id);
        
      if (availabilityError) {
        toast({
          title: "Erro",
          description: "Erro ao carregar sua disponibilidade",
          variant: "destructive",
        });
        return;
      }
      
      if (availabilityData && availabilityData.length > 0) {
        // Convert database data to our state format
        const scheduledDays: DaySchedule[] = [];
        
        availabilityData.forEach(item => {
          const existingDayIndex = scheduledDays.findIndex(day => day.day === item.day_of_week);
          
          if (existingDayIndex >= 0) {
            // Add slot to existing day
            scheduledDays[existingDayIndex].slots.push({
              id: item.id,
              start: item.start_time,
              end: item.end_time
            });
          } else {
            // Create new day entry
            scheduledDays.push({
              day: item.day_of_week,
              slots: [{
                id: item.id,
                start: item.start_time,
                end: item.end_time
              }]
            });
          }
        });
        
        setWeeklySchedule(scheduledDays);
      }
    } catch (error) {
      console.error("Error fetching mentor availability:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar sua disponibilidade",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDaySelect = (dayIndex: number) => {
    setSelectedDay(dayIndex);
    
    // Check if we already have slots for this day
    const existingDay = weeklySchedule.find(day => day.day === dayIndex);
    if (existingDay) {
      setTimeSlots([...existingDay.slots]);
    } else {
      setTimeSlots([{ start: "09:00", end: "18:00" }]);
    }
  };

  const handleSaveTimeSlots = async () => {
    if (selectedDay === null) {
      toast({
        title: "Erro",
        description: "Selecione um dia da semana",
        variant: "destructive",
      });
      return;
    }
    
    // Validate time slots
    for (const slot of timeSlots) {
      if (slot.start >= slot.end) {
        toast({
          title: "Erro",
          description: "O horário de início deve ser anterior ao horário de fim",
          variant: "destructive",
        });
        return;
      }
    }
    
    // Check for overlapping time slots
    for (let i = 0; i < timeSlots.length; i++) {
      for (let j = i + 1; j < timeSlots.length; j++) {
        const slotA = timeSlots[i];
        const slotB = timeSlots[j];
        
        if (
          (slotA.start <= slotB.start && slotB.start < slotA.end) ||
          (slotB.start <= slotA.start && slotA.start < slotB.end)
        ) {
          toast({
            title: "Erro",
            description: "Existem intervalos de horário sobrepostos",
            variant: "destructive",
          });
          return;
        }
      }
    }
    
    try {
      setSavingDay(true);
      
      // Get user session and mentor ID
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      if (!sessionData?.session?.user?.id) {
        toast({
          title: "Erro",
          description: "Você precisa estar logado para salvar sua disponibilidade",
          variant: "destructive",
        });
        return;
      }
      
      // Get mentor ID from profile
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('id')
        .eq('profile_id', sessionData.session.user.id)
        .single();
        
      if (mentorError || !mentorData) {
        toast({
          title: "Erro",
          description: "Não foi possível encontrar suas informações de mentor",
          variant: "destructive",
        });
        return;
      }
      
      // Find existing day in the schedule
      const existingDayIndex = weeklySchedule.findIndex(day => day.day === selectedDay);
      
      // First, remove any existing entries for this day from the database
      const existingDayData = existingDayIndex >= 0 ? weeklySchedule[existingDayIndex] : null;
      if (existingDayData) {
        for (const slot of existingDayData.slots) {
          if (slot.id) {
            await supabase
              .from('mentor_availability')
              .delete()
              .eq('id', slot.id);
          }
        }
      }
      
      // Now insert all new time slots for this day
      const insertPromises = timeSlots.map(slot => {
        return supabase
          .from('mentor_availability')
          .insert({
            mentor_id: mentorData.id,
            day_of_week: selectedDay,
            start_time: slot.start,
            end_time: slot.end
          });
      });
      
      await Promise.all(insertPromises);
      
      // Update the local state
      const updatedSchedule = [...weeklySchedule];
      if (existingDayIndex >= 0) {
        // Update existing day
        updatedSchedule[existingDayIndex] = {
          day: selectedDay,
          slots: [...timeSlots]
        };
      } else {
        // Add new day
        updatedSchedule.push({
          day: selectedDay,
          slots: [...timeSlots]
        });
      }
      
      setWeeklySchedule(updatedSchedule);
      
      toast({
        title: "Sucesso",
        description: `Disponibilidade para ${dayNames[selectedDay]} salva com sucesso!`,
      });
      
      // Refresh data from server
      fetchMentorAvailability();
      
    } catch (error) {
      console.error("Error saving availability:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar sua disponibilidade",
        variant: "destructive",
      });
    } finally {
      setSavingDay(false);
    }
  };

  return {
    selectedDay,
    weeklySchedule,
    timeSlots,
    loading,
    savingDay,
    setTimeSlots,
    handleDaySelect,
    handleSaveTimeSlots,
    fetchMentorAvailability
  };
};

const dayNames = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
export { dayNames };
